import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { CreateRegistryRequest, ListRegistriesResponse, Registry, RegistryFormData, RegistryOperationsRequest } from 'src/app/core/models/repository';
import { ConfigEntityService } from 'src/app/core/services/config-entity.service';
import { CreateRegistryComponent } from '../modal/create-registry/create-registry.component';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { hostname } from 'os';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'app-registry-management',
  templateUrl: './registry-management.component.html',
  styleUrls: ['./registry-management.component.scss']
})

export class RegistryManagementComponent implements OnInit {
  registriesData: ListRegistriesResponse | null = null;
  filteredRegistries: Registry[] = [];
  searchTerm = '';
  showCreateForm = false;
  isLoading = false;
  isCreating = false;
  successMessage = '';
  errorMessage = '';

  constructor(
    private configEntityService: ConfigEntityService,
    public dialog: MatDialog,
    private snackbar: MatSnackBar,
    private spinner: NgxSpinnerService
  ) {
  }

  ngOnInit() {
    this.loadRegistries();
  }

  loadRegistries() {
    try{
      this.spinner.show();
      this.isLoading = true;
      this.configEntityService.listRegistries().subscribe({
        next: (data) => {
          this.spinner.hide();
          this.registriesData = data;
          this.filteredRegistries = [...data.registries];
          this.isLoading = false;
        },
        error: (error) => {
          this.spinner.hide();
          this.isLoading = false;
        }
      });
    }
    catch (error) {
      this.spinner.hide();
      console.error('Error initializing registry management:', error);
      this.isLoading = false;
      this.snackbar.open('Failed to load registries', 'Close', {
        duration: 5000,
      });
      return;
    }
  }

  createRegistry() {
    const dialogRef = this.dialog.open(CreateRegistryComponent, {
        width: "50%",
        autoFocus: true,
        disableClose: true,
        hasBackdrop: false,
        panelClass: "job-dialog-containers",
      });
      dialogRef.afterClosed().subscribe((res) => {
        if (res) {
          this.loadRegistries();
        }
    });
  }

  startRegistry(registry: Registry) {
    try{
      if (registry.status === 'running') {
        this.spinner.hide();
        this.snackbar.open('Registry is already running', 'Close', {
          duration: 5000,
        });
        return;
      }
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: "50%",
        autoFocus: false,
        disableClose: false,
        data: {
          titleMessage: 'Start Registry',
          bodyMessage: `Are you sure you want to start the registry '${registry.name}'?`,
          cancelText: "Cancel",
          submitText: "Start",
        }
      });
      dialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed) {
          this.spinner.show();
          let request: RegistryOperationsRequest = {
            registryName: registry.name,
            action: 'start',
            hostName: ''
          };
          this.configEntityService.registryOperation(request).subscribe({
            next: (response) => {
              this.spinner.hide();
              this.successMessage = JSON.parse(JSON.stringify(response)).message;
              this.snackbar.open(this.successMessage, 'Close', {
                duration: 5000,
              });
              this.loadRegistries();
            },
            error: (error) => {
              this.spinner.hide();
              this.errorMessage = 'Failed to start registry';
              console.error('Error starting registry:', error);
            }
          });
        }
      });
    }
    catch (error) {
      this.spinner.hide();
      console.error('Error starting registry:', error);
      this.snackbar.open('Failed to start registry', 'Close', {
        duration: 5000,
      });
      return;
    }
  }

  stopRegistry(registry: Registry) {
    try{
      if (registry.status === 'stopped') {
        this.spinner.hide();
        this.snackbar.open('Registry is already stopped', 'Close', {
          duration: 5000,
        });
        return;
      }

      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: "50%",
        data: {
          titleMessage: 'Stop Registry',
          bodyMessage: `Are you sure you want to stop the registry '${registry.name}'?`,
          cancelText: "Cancel",
          submitText: "Stop",
        }
      });
      dialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed) {
          this.spinner.show();
          let request: RegistryOperationsRequest = {
            registryName: registry.name,
            action: 'stop',
            hostName: ''
          };
          this.configEntityService.registryOperation(request).subscribe({
            next: (response) => {
              this.spinner.hide();
              this.successMessage = JSON.parse(JSON.stringify(response)).message;
              this.snackbar.open(this.successMessage, 'Close', {
                duration: 5000,
              });
              this.loadRegistries();
            },
            error: (error) => {
              this.spinner.hide();
              this.errorMessage = 'Failed to stop registry';
              console.error('Error stopping registry:', error);
            }
          });
        }
      });
    }
    catch (error) {
      this.spinner.hide();
      console.error('Error stopping registry:', error);
      this.snackbar.open('Failed to stop registry', 'Close', {
        duration: 5000,
      });
      return;
    }
  }

  deleteRegistry(registry: Registry) {
    try{
      if (registry.status === 'running') {
        this.spinner.hide();
        this.snackbar.open('Please stop the registry before deleting', 'Close', {
          duration: 5000,
        });
        return;
      }
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: "50%",
        data: {
          titleMessage: 'Delete Registry',
          bodyMessage: `Are you sure you want to delete the registry '${registry.name}'?`,
          cancelText: "Cancel",
          submitText: "Delete",
        }
      });
      dialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed) {
          this.spinner.show();
          let request: RegistryOperationsRequest = {
            registryName: registry.name,
            action: 'delete',
            hostName: ''
          };
          this.configEntityService.registryOperation(request).subscribe({
            next: (response) => {
              this.spinner.hide();
              this.successMessage = JSON.parse(JSON.stringify(response)).message;
              this.snackbar.open(this.successMessage, 'Close', {
                duration: 5000,
              });
              this.loadRegistries();
            },
            error: (error) => {
              this.spinner.hide();
              this.errorMessage = 'Failed to delete registry';
              console.error('Error deleting registry:', error);
            }
          });
        }
      });
    }
    catch (error) {
      this.spinner.hide();
      console.error('Error deleting registry:', error);
      this.snackbar.open('Failed to delete registry', 'Close', {
        duration: 5000,
      });
      return;
    } 
  }

  restartRegistry(registry: Registry) {
    try{
      this.spinner.show();
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: "50%",
        autoFocus: false,
        disableClose: false,
        data: {
          titleMessage: 'Restart Registry',
          bodyMessage: `Are you sure you want to restart the registry '${registry.name}'?`,
          cancelText: "Cancel",
          submitText: "Restart",
        }
      });
      dialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed) {
          let request: RegistryOperationsRequest = {
            registryName: registry.name,
            action: 'restart',
            hostName: ''
          };
          this.configEntityService.registryOperation(request).subscribe({
            next: (response) => {
              this.spinner.hide();
              this.successMessage = JSON.parse(JSON.stringify(response)).message;
              this.snackbar.open(this.successMessage, 'Close', {
                duration: 5000,
              });
              this.loadRegistries();
            },
            error: (error) => {
              this.spinner.hide();
              this.errorMessage = 'Failed to restart registry';
              console.error('Error restarting registry:', error);
            }
          });
        }
      });
    }
    catch (error) {
      this.spinner.hide();
      console.error('Error restarting registry:', error);
      this.snackbar.open('Failed to restart registry', 'Close', {
        duration: 5000,
      });
      return;
    } 
  }

  filterRegistries() {
    if (!this.registriesData) return;
    
    this.filteredRegistries = this.registriesData.registries.filter(registry =>
      registry.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
      registry.status.toLowerCase().includes(this.searchTerm.toLowerCase())
    );
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
}
